import { Event, LocalAdapter, Storage, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Action, Button, Icon, InputOption, Modal, Pressable, styles, SWITCH, Text, useDevice, View } from '@mirai/ui';
import React, { useEffect, useState } from 'react';

import { DENIED, GRANTED, KEY_COOKIES_ACCEPTED, KEY_COOKIES_ACCEPTED_LEGACY, POLICIES } from './CookieBanner.constants';
import { L10N } from './CookieBanner.l10n';
import * as style from './CookieBanner.module.css';
import { getExternalCookie } from './helpers';
import { EVENT, ICON } from '../helpers';

const { GOOGLE, META, MICROSOFT, X, YANDEX } = POLICIES;

const storage = new Storage({ adapter: LocalAdapter });

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

const CookieBanner = () => {
  const { isMobile } = useDevice();
  const { translate } = useLocale();

  const { value: { cookieBanner: { active, countryInactive } = {}, skeleton = false } = {} } = useStore();

  const [accepted, setAccepted] = useState();
  const [expanded, setExpanded] = useState([false, false]);
  const [settings, setSettings] = useState();
  const [settingsAccepted, setSettingsAccepted] = useState(false);
  const [visible, setVisible] = useState();

  useEffect(() => {
    if (!active && !countryInactive) return;

    gtag('consent', 'default', {
      ad_storage: DENIED,
      ad_user_data: DENIED,
      ad_personalization: DENIED,
      analytics_storage: DENIED,
    });
    const value = getValue();
    if (value || countryInactive) {
      setAccepted(value === GRANTED || countryInactive);
    } else {
      setVisible(true);
    }
  }, [active, countryInactive]);

  useEffect(() => {
    if (skeleton || !active) return;

    Event.subscribe(EVENT.COOKIE_BANNER_OPEN, handleOpenSettings);
    return () => Event.unsubscribe(EVENT.COOKIE_BANNER_OPEN, handleOpenSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, skeleton]);

  useEffect(() => {
    if (accepted === undefined) return;

    const status = accepted ? GRANTED : DENIED;

    gtag('consent', 'update', {
      ad_storage: status,
      ad_user_data: status,
      ad_personalization: status,
      analytics_storage: status,
    });
    if (getExternalCookie() === undefined) {
      storage.set(KEY_COOKIES_ACCEPTED, status);
      localStorage.setItem(KEY_COOKIES_ACCEPTED_LEGACY, status);
    }
  }, [accepted]);

  const handleOpenSettings = () => {
    setSettingsAccepted(getValue() === GRANTED);
    setExpanded([false, false]);
    setSettings(true);
    setVisible(true);
  };

  const handleResponse = (value) => {
    setAccepted(value);
    setVisible(false);
  };

  const getValue = () =>
    storage.get(KEY_COOKIES_ACCEPTED) || localStorage.getItem(KEY_COOKIES_ACCEPTED_LEGACY) || getExternalCookie();

  const [mandatory, analytical] = expanded || [];
  const actionProps = { target: '_blank', underline: true };

  return active ? (
    <Modal
      fit
      portal
      title={translate(settings ? L10N.LABEL_COOKIES_SETTINGS : L10N.LABEL_COOKIES_PREFERENCES)}
      visible={visible}
      className={style.modal}
      testId="cookieBanner"
    >
      <View className={style.content}>
        {settings ? (
          <>
            <Text action>{translate(L10N.LABEL_COOKIES_SETTINGS_CONTENT)}</Text>
            <Text action>{translate(L10N.LABEL_COOKIES_SETTINGS_INFO)}</Text>
            <Text action>{translate(L10N.LABEL_COOKIES_SETTINGS_PRIVACY)}</Text>
            <View className={style.settings}>
              <Text bold>{translate(L10N.LABEL_CUSTOMIZE_CHOICES)}</Text>
              <View>
                <View>
                  <View row className={style.setting}>
                    <Pressable
                      row
                      onPress={() => setExpanded([!mandatory, analytical])}
                      testId="cookieBanner-mandatory"
                    >
                      <Icon value={mandatory ? ICON.EXPAND_LESS : ICON.EXPAND_MORE} />
                      <Text action bold>
                        {translate(L10N.LABEL_MANDATORY_COOKIES_TITLE)}
                      </Text>
                    </Pressable>
                    <Text action bold success>
                      {translate(L10N.LABEL_ALWAYS_ACTIVE)}
                    </Text>
                  </View>
                  {mandatory && (
                    <Text action className={style.info}>
                      {translate(L10N.LABEL_MANDATORY_COOKIES_CONTENT)}
                    </Text>
                  )}
                </View>
                <View>
                  <View row className={styles(style.setting, style.last)}>
                    <Pressable
                      row
                      onPress={() => setExpanded([mandatory, !analytical])}
                      testId="cookieBanner-analytical"
                    >
                      <Icon value={analytical ? ICON.EXPAND_LESS : ICON.EXPAND_MORE} />
                      <Text action bold>
                        {translate(L10N.LABEL_ANALYTICAL_COOKIES_TITLE)}
                      </Text>
                    </Pressable>
                    <InputOption
                      checked={settingsAccepted}
                      name="accept"
                      small
                      type={SWITCH}
                      onChange={setSettingsAccepted}
                    />
                  </View>
                  {analytical && (
                    <Text action className={style.info}>
                      {translate(L10N.LABEL_ANALYTICAL_COOKIES_CONTENT, {
                        google: (
                          <Action href={GOOGLE.url} {...actionProps}>
                            {GOOGLE.text}
                          </Action>
                        ),
                        meta: (
                          <Action href={META.url} {...actionProps}>
                            {META.text}
                          </Action>
                        ),
                        microsoft: (
                          <Action href={MICROSOFT.url} {...actionProps}>
                            {MICROSOFT.text}
                          </Action>
                        ),
                        x: (
                          <Action href={X.url} {...actionProps}>
                            {X.text}
                          </Action>
                        ),
                        yandex: (
                          <Action href={YANDEX.url} {...actionProps}>
                            {YANDEX.text}
                          </Action>
                        ),
                      })}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          </>
        ) : (
          <>
            <Text action>{translate(L10N.LABEL_COOKIES_CONTENT)}</Text>
            <Text action>
              {translate(L10N.LABEL_COOKIES_MORE_INFO, {
                here: (
                  <Action onPress={() => setSettings(true)} className={style.action}>
                    {translate(L10N.LABEL_HERE)}
                  </Action>
                ),
              })}
            </Text>
          </>
        )}
      </View>
      <View row={!isMobile} wide={isMobile} className={styles(style.buttons, settings && style.settings)}>
        {settings ? (
          <>
            <Button onPress={() => handleResponse(false)}>{translate(L10N.ACTION_REJECT_ALL)}</Button>
            <Button onPress={() => handleResponse(settingsAccepted)}>{translate(L10N.ACTION_SAVE)}</Button>
          </>
        ) : (
          <>
            <Action onPress={() => setSettings(true)} testId="cookieBanner-settings">
              {translate(L10N.ACTION_SETTINGS)}
            </Action>
            <View row={!isMobile} wide={isMobile}>
              <Button onPress={() => handleResponse(false)}>{translate(L10N.ACTION_REJECT)}</Button>
              <Button onPress={() => handleResponse(true)}>{translate(L10N.ACTION_ACCEPT)}</Button>
            </View>
          </>
        )}
      </View>
    </Modal>
  ) : null;
};

CookieBanner.displayName = 'Mirai:Core:CookieBanner';

export { CookieBanner };
